import { useEffect, useState } from 'react';
import quoteService, { type QuoteAclKey } from '../services/quote.service';

/**
 * Quote field/action ACLs (P18 #46) — the keys the current user holds, fetched
 * from GET /quotes/acls so the resolution stays server-side (dormant → all keys,
 * admin/impersonation-aware).
 *
 * FAIL-OPEN: the set defaults to every key and stays that way while the fetch is
 * in flight or if it errors. The UI therefore shows exactly what it shows today
 * until ops gates a key, and a permissive default can never grant a real action
 * — every gate is also enforced on the server (403), so the client copy is only
 * a UX affordance (hide/disable rather than let the user hit an error).
 */
export const QUOTE_ACL_KEYS: QuoteAclKey[] = [
  'costing', 'dupe', 'newrevision', 'bypassminimumrate',
  'viewtariff', 'edittariff', 'intelligenceinfo', 'history_unmask',
];

export interface QuoteAcls {
  has: (key: QuoteAclKey) => boolean;
  acls: Set<QuoteAclKey>;
  loading: boolean;
}

export function useQuoteAcls(): QuoteAcls {
  const [acls, setAcls] = useState<Set<QuoteAclKey>>(new Set(QUOTE_ACL_KEYS));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // Promise.resolve().then(...) so a missing/throwing service method becomes a
    // rejection (fail-open) instead of an uncaught synchronous error.
    Promise.resolve()
      .then(() => quoteService.getQuoteAcls())
      .then((keys) => { if (alive && Array.isArray(keys)) setAcls(new Set(keys)); })
      .catch(() => { /* fail-open: keep the permissive default */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return { has: (key) => acls.has(key), acls, loading };
}
